import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import HelpIcon from "@mui/icons-material/Help";
import moment from "moment";
import {
  FETCH_CLIENT_INSPECTIONS_FORM_TEMPLATES_2,
  FETCH_CLIENT_INSPECTION_FORM_ANSWERS_2
} from "../../../constant/Apipath";
import { Postrequestcall } from "../../../request/Postrequest";
import Loader from "../../../common/Loader";
import { SectionViewModal } from "../../inspection/SectionViewModal";
import { BIBlue } from "../../../assets/helpers";

const FixedAssetInspection = ({ selectedAsset, refreshToken }) => {
  const { loginData, selectedClientData } = useSelector((state) => state?.main);

  const userId = loginData?.userInformation?.UserId;
  const clientId = selectedClientData?.ClientId;
  const clientEmployeeId = selectedClientData?.ClientEmployeeId;
  const accentColor = selectedClientData?.AccentColor || BIBlue;

  const assetId = selectedAsset?.AssetId || selectedAsset?.id;
  const categoryId = Number(
    selectedAsset?.CategoryId ??
    selectedAsset?.AssetCategoryId ??
    selectedAsset?.assetCategoryId ??
    selectedAsset?.categoryId ??
    selectedAsset?.assetCategory ??
    0
  );
  const categoryName =
    selectedAsset?.Category ??
    selectedAsset?.CategoryName ??
    selectedAsset?.categoryName ??
    selectedAsset?.assetCategoryName ??
    "";

  // List states
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(false);

  // Type selection modal states
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [inspectionTypes, setInspectionTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [typeSearch, setTypeSearch] = useState("");

  // Section / Questions inspection modal
  const [openSectionModal, setOpenSectionModal] = useState(false);
  const [selectedInspectionItem, setSelectedInspectionItem] = useState({});

  // -------------------------------------------------------------
  // 1. Fetch Inspections for this Fixed Asset
  // -------------------------------------------------------------
  const fetchInspections = useCallback(async () => {
    if (!assetId || !clientId) return;
    try {
      setLoading(true);
      const payload = {
        PageNr: 1,
        NrOfRecPerPage: 50,
        FullSearch: "",
        UserId: Number(userId),
        IncludeRecordNr: true,
        FetchAllowedRecordsOnly: false,
        SortList: [{ FieldName: "DateModified", Direction: "DESC" }],
        SearchList: [
          {
            ClientId: Number(clientId),
            // employeeId: Number(clientEmployeeId),
            FixedAssetId: Number(assetId),
          }
        ]
      };

      const res = await Postrequestcall(
        FETCH_CLIENT_INSPECTION_FORM_ANSWERS_2,
        payload,
        loginData?.accessToken
      );

      if (res?.data?.data && res?.data?.status === true) {
        setInspections(res?.data?.data || []);
      } else {
        setInspections([]);
      }
    } catch (err) {
      console.error("fetchInspections error:", err);
      setInspections([]);
    } finally {
      setLoading(false);
    }
  }, [assetId, clientId, userId, loginData?.accessToken]);

  useEffect(() => {
    fetchInspections();
  }, [fetchInspections, refreshToken]);

  // -------------------------------------------------------------
  // 2. Fetch Inspection Form Templates
  // -------------------------------------------------------------
  const fetchInspectionTypes = async (searchVal = "") => {
    if (!clientId) return;
    try {
      setTypesLoading(true);
      const payload = {
        PageNr: 1,
        NrOfRecPerPage: 100,
        FullSearch: searchVal,
        UserId: Number(userId),
        IncludeRecordNr: true,
        FetchAllowedRecordsOnly: true,
        DoNotSearchInSystemFields: true,
        SearchList: [
          {
            ClientId: Number(clientId),
            ...(categoryId ? {  FixedAssetCategoryId: Number(categoryId) } : {})
          }
        ],
        SortList: [{ FieldName: "id", Direction: "DESC" }]
      };

      const res = await Postrequestcall(
        FETCH_CLIENT_INSPECTIONS_FORM_TEMPLATES_2,
        payload,
        loginData?.accessToken
      );

      if (res?.status === 200 && res?.data?.data) {
        setInspectionTypes(res?.data?.data || []);
      } else {
        setInspectionTypes([]);
      }
    } catch (err) {
      console.error("fetchInspectionTypes error:", err);
      setInspectionTypes([]);
    } finally {
      setTypesLoading(false);
    }
  };

  const handleOpenTypeModal = () => {
    setTypeSearch("");
    setTypeModalOpen(true);
    fetchInspectionTypes("");
  };

  const handleSelectInspectionType = (type) => {
    setTypeModalOpen(false);
    setSelectedInspectionItem({
      id: 0,
      formId: type.id,
      formName: type.title || type.name || type.formName || "Inspection",
      isSubmit: false,
      FixedAssetId: Number(assetId),
      isFixedAssetForm: true,
    });
    setOpenSectionModal(true);
  };

  const handleInspectionClick = (item) => {
    setSelectedInspectionItem({
      ...item,
      formId: item.formId || item.id,
      FixedAssetId: Number(assetId),
      isFixedAssetForm: true,
    });
    setOpenSectionModal(true);
  };

  return (
    <Box sx={{ pt: 1 }}>
      {/* Header Bar */}
      <Stack sx={{ justifyContent: "space-between", alignItems: "center" }} direction="row">
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Inspection History
        </Typography>
        <Button
          variant="contained"
          onClick={handleOpenTypeModal}
          sx={{
            backgroundColor: accentColor,
            color: "#fff",
            boxShadow: "none",
            textTransform: "none"
          }}>
          New Inspection
        </Button>
      </Stack>

      {/* List / Empty Content */}
      {loading ? (
        <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
          <Loader />
        </Box>
      ) : (
        <Stack spacing={1} sx={{ mt: 2 }}>
          {inspections.length === 0 ? (
            <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", py: 3 }}>
              No inspection records yet.
            </Typography>
          ) : (
            inspections.map((item) => (
              <Paper
                key={item.id}
                variant="outlined"
                onClick={() => handleInspectionClick(item)}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                  transition: "0.15s ease",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                    borderColor: accentColor
                  }
                }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {item.formName || item.title || item.name || item.inspectionTypeName || "Inspection"}
                  </Typography>
                  <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                    {item?.employeeName && (
                      <span>
                        Submitted by: <strong>{item?.employeeName}</strong>
                        {" | "}
                      </span>
                    )}
                    {item.clientJobCode && item.clientJobName
                      ? `Job: ${item.clientJobCode} - ${item.clientJobName} | `
                      : ""}
                    Date: {moment(item.dateModified || item.date || item.dateCreated).format("MMM DD, YYYY")}
                  </Typography>
                  {item.comments && (
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                      Comments: {item.comments}
                    </Typography>
                  )}
                </Box>
                <Chip
                  size="small"
                  label={item?.isSubmit || item?.isSubmitted ? "Submitted" : "Draft"}
                  color={item?.isSubmit || item?.isSubmitted ? "success" : "warning"}
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </Paper>
            ))
          )}
        </Stack>
      )}

      {/* ========================================================= */}
      {/* 1. Modal: Choose Inspection Form                          */}
      {/* ========================================================= */}
      <Dialog
        open={typeModalOpen}
        onClose={() => setTypeModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Choose Inspection Form</span>
          <IconButton size="small" onClick={() => setTypeModalOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 2.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search inspection forms..."
            value={typeSearch}
            onChange={(e) => {
              setTypeSearch(e.target.value);
              fetchInspectionTypes(e.target.value);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              )
            }}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />

          {typesLoading ? (
            <Stack spacing={1.5}>
              {[1, 2, 3, 4].map((n) => (
                <Skeleton key={n} variant="rectangular" height={54} sx={{ borderRadius: 2 }} />
              ))}
            </Stack>
          ) : inspectionTypes.length === 0 ? (
            <Box
              sx={{
                py: 5,
                px: 3,
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center"
              }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  bgcolor: "#f1f5f9",
                  color: "#64748b",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2
                }}>
                <HelpIcon sx={{ fontSize: 32 }} />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#1f2937", mb: 0.5 }}>
                No inspection forms available for this client
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.2}>
              {inspectionTypes
                .filter(
                  (t) =>
                    !typeSearch ||
                    (t.title || t.name || t.inspectionTypeName || t.formName || "")
                      .toLowerCase()
                      .includes(typeSearch.toLowerCase())
                )
                .map((type) => (
                  <Paper
                    key={type.id}
                    variant="outlined"
                    onClick={() => handleSelectInspectionType(type)}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      transition: "all 0.15s ease",
                      "&:hover": {
                        borderColor: accentColor,
                        bgcolor: "#f0f7ff"
                      }
                    }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#1f2937" }}>
                        {type.title || type.name || type.inspectionTypeName || type.formName}
                      </Typography>
                      {(type.description || type.formDescription) && (
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {type.description || type.formDescription}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      size="small"
                      label="Select"
                      variant="outlined"
                      sx={{
                        fontWeight: 600,
                        color: accentColor,
                        borderColor: accentColor
                      }}
                    />
                  </Paper>
                ))}
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* ========================================================= */}
      {/* 2. Full Inspection Section & Questions Form Modal         */}
      {/* ========================================================= */}
      {openSectionModal && (
        <SectionViewModal
          open={openSectionModal}
          onClose={() => setOpenSectionModal(false)}
          selectedItem={selectedInspectionItem}
          getList={fetchInspections}
        />
      )}
    </Box>
  );
};

export default FixedAssetInspection;
